import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  TextField,
  Switch,
  Button,
  Radio,
  RadioGroup,
  Card,
  CardContent,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useGetSettingsQuery, useUpdateSettingsMutation } from 'state/api';
import { useSnack, SaveSnackbar } from './settingsHelper';

const VendorSetup = () => {
  const { data } = useGetSettingsQuery();
  const settings = data?.data || {};
  const [updateSettings, { isLoading }] = useUpdateSettingsMutation();
  const { snack, showSuccess, showError, closeSnack } = useSnack();

  const [defaultCommission, setDefaultCommission] = useState(15);
  const [enablePOS, setEnablePOS] = useState(false);
  const [vendorRegistration, setVendorRegistration] = useState(false);
  const [forgotPasswordVerification, setForgotPasswordVerification] = useState('Email');
  const [needProductApproval, setNeedProductApproval] = useState(false);
  const [newProduct, setNewProduct] = useState(false);
  const [productWiseShippingCost, setProductWiseShippingCost] = useState(false);
  const [minimumOrderAmountEnabled, setMinimumOrderAmountEnabled] = useState(false);
  const [vendorCanReplyOnReview, setVendorCanReplyOnReview] = useState(false);

  useEffect(() => {
    const v = settings.custom?.vendorSetup;
    if (!v) return;
    if (v.defaultCommission !== undefined) setDefaultCommission(v.defaultCommission);
    if (v.enablePOS !== undefined) setEnablePOS(v.enablePOS);
    if (v.vendorRegistration !== undefined) setVendorRegistration(v.vendorRegistration);
    if (v.forgotPasswordVerification !== undefined) setForgotPasswordVerification(v.forgotPasswordVerification);
    if (v.needProductApproval !== undefined) setNeedProductApproval(v.needProductApproval);
    if (v.newProduct !== undefined) setNewProduct(v.newProduct);
    if (v.productWiseShippingCost !== undefined) setProductWiseShippingCost(v.productWiseShippingCost);
    if (v.minimumOrderAmountEnabled !== undefined) setMinimumOrderAmountEnabled(v.minimumOrderAmountEnabled);
    if (v.vendorCanReplyOnReview !== undefined) setVendorCanReplyOnReview(v.vendorCanReplyOnReview);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleDefaultCommissionChange = (event) => setDefaultCommission(event.target.value);
  const handleEnablePOSChange = (event) => setEnablePOS(event.target.checked);
  const handleVendorRegistrationChange = (event) => setVendorRegistration(event.target.checked);
  const handleForgotPasswordVerificationChange = (event) => setForgotPasswordVerification(event.target.value);
  const handleNewProductChange = (event) => setNewProduct(event.target.checked);
  const handleProductWiseShippingCostChange = (event) => setProductWiseShippingCost(event.target.checked);
  const handleSetMinimumOrderAmountChange = (event) => setMinimumOrderAmountEnabled(event.target.checked);
  const handleVendorCanReplyOnReviewChange = (event) => setVendorCanReplyOnReview(event.target.checked);

  const handleSave = async () => {
    try {
      await updateSettings({
        custom: {
          vendorSetup: {
            defaultCommission,
            enablePOS,
            vendorRegistration,
            forgotPasswordVerification,
            needProductApproval,
            newProduct,
            productWiseShippingCost,
            minimumOrderAmountEnabled,
            vendorCanReplyOnReview,
          },
        },
      }).unwrap();
      showSuccess();
    } catch (err) {
      showError(err?.data?.error?.message);
    }
  };

  return (
    <Card variant="outlined" sx={{ borderColor: 'divider', bgcolor: 'background.paper' }}>
      <CardContent>
        <Typography variant="h6" color="text.primary" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ShoppingCartIcon sx={{ mr: 1, color: 'primary.main' }} /> Vendor Setup
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Default Commission"
              type="number"
              value={defaultCommission}
              onChange={handleDefaultCommissionChange}
              InputProps={{ inputProps: { min: 0, max: 100 }, endAdornment: <InfoIcon color="primary" /> }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body1" color="text.primary">
              Enable POS in Vendor Panel
              <InfoIcon color="primary" />
            </Typography>
            <Switch checked={enablePOS} onChange={handleEnablePOSChange} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body1" color="text.primary">
              Vendor registration
              <InfoIcon color="primary" />
            </Typography>
            <Switch checked={vendorRegistration} onChange={handleVendorRegistrationChange} />
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="body1" color="text.primary">
              Set minimum order amount
              <InfoIcon color="primary" />
            </Typography>
            <Switch checked={minimumOrderAmountEnabled} onChange={handleSetMinimumOrderAmountChange} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body1" color="text.primary">
              Vendor Can Reply on Review
              <InfoIcon color="primary" />
            </Typography>
            <Switch checked={vendorCanReplyOnReview} onChange={handleVendorCanReplyOnReviewChange} />
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="body1" color="text.primary">
              Forgot Password Verification By
              <InfoIcon color="primary" />
            </Typography>
            <RadioGroup
              row
              aria-labelledby="forgot-password-verification-group"
              value={forgotPasswordVerification}
              onChange={handleForgotPasswordVerificationChange}
            >
              <FormControlLabel value="Email" control={<Radio />} label="Email" />
              <FormControlLabel value="Phone (OTP)" control={<Radio />} label="Phone (OTP)" />
            </RadioGroup>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="h6" color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
              <ShoppingCartIcon sx={{ mr: 1, color: 'primary.main' }} /> Need Product Approval
              <InfoIcon color="primary" />
            </Typography>
            <Switch checked={needProductApproval} onChange={(e) => setNeedProductApproval(e.target.checked)} />
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="body1" color="text.primary">
              New Product
            </Typography>
            <Switch checked={newProduct} onChange={handleNewProductChange} />
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="body1" color="text.primary">
              Product Wise Shipping Cost (This Feature Will Activate Whenever A Vendor Add A Product Or Modifies The Shipping Cost Of Any Product.)
            </Typography>
            <Switch checked={productWiseShippingCost} onChange={handleProductWiseShippingCostChange} />
          </Grid>
        </Grid>

        <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
        </Grid>

        <SaveSnackbar snack={snack} onClose={closeSnack} />
      </CardContent>
    </Card>
  );
};

export default VendorSetup;
